import React, { useMemo } from 'react';
import { useGantt } from '../context/GanttContext';
import { generateDateHeaders } from '../utils/dateUtils';
import { HEADER_HEIGHT } from '../types/gantt.types';

interface TimelineHeaderProps {
  scrollLeft: number;
}

const TimelineHeader: React.FC<TimelineHeaderProps> = ({ scrollLeft }) => {
  const { timelineConfig } = useGantt();
  const { primary, secondary } = useMemo(
    () => generateDateHeaders(timelineConfig),
    [timelineConfig]
  );

  const totalWidth = timelineConfig.totalDays * timelineConfig.dayWidth;

  return (
    <div
      style={{
        height: HEADER_HEIGHT,
        background: '#fafafa',
        borderBottom: '1px solid #d9d9d9',
        overflow: 'hidden',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}
    >
      <div
        style={{
          transform: `translateX(-${scrollLeft}px)`,
          display: 'flex',
          flexDirection: 'column',
          width: totalWidth,
        }}
      >
        {/* Both rows are positioned off the same pixel offsets the SVG uses,
            rather than flowed — at fractional dayWidth (quarter/year zoom)
            accumulated flex rounding would drift the labels off the bars. */}

        {/* Primary row (Months / Years / Decades) */}
        <div
          style={{
            position: 'relative',
            height: 28,
            width: totalWidth,
            borderBottom: '1px solid #e8e8e8',
          }}
        >
          {primary.map((header, index) => (
            <div
              key={`primary-${index}`}
              style={{
                position: 'absolute',
                left: header.x,
                width: header.width,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRight: '1px solid #e8e8e8',
                fontSize: 12,
                fontWeight: 600,
                color: '#262626',
                background: '#f5f5f5',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {header.label}
            </div>
          ))}
        </div>

        {/* Secondary row (Days / Weeks / Months / Quarters / Years) */}
        <div
          style={{
            position: 'relative',
            height: 30,
            width: totalWidth,
          }}
        >
          {secondary.map((header, index) => (
            <div
              key={`secondary-${index}`}
              style={{
                position: 'absolute',
                left: header.x,
                width: header.width,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRight: '1px solid #f0f0f0',
                fontSize: 11,
                fontWeight: header.isToday ? 600 : 400,
                color: header.isToday ? '#1890ff' : header.isWeekend ? '#8c8c8c' : '#595959',
                background: header.isToday
                  ? 'rgba(24, 144, 255, 0.1)'
                  : header.isWeekend
                  ? '#fafafa'
                  : 'transparent',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {header.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TimelineHeader;
